import { pluralizeWithCount } from "@/lib/utils";

export function defaultNudgeMsg(target, pastDeadline, slack) {
  if (target === "parent_dri") {
    if (pastDeadline) {
      return `Hey ${
        slack ? "<@RECIPIENT>" : "there"
      }, the snapshot is about to be closed and there are teams that you're over with individuals that haven't filled it out yet. Could you follow up with the team leads to make sure folks respond ASAP?`;
    }

    return `Hey ${
      slack ? "<@RECIPIENT>" : "there"
    }, it looks like there are teams that you're over with individuals that haven't filled it out yet. Could you follow up with the team leads to make sure folks respond?`;
  }

  if (target === "leaf_dri") {
    if (pastDeadline) {
      return `Hey ${
        slack ? "<@RECIPIENT>" : "there"
      }, the snapshot is about to be closed and there are folks from your team that haven't filled it out yet. Could you follow up to make sure folks respond ASAP?`;
    }

    return `Hey ${
      slack ? "<@RECIPIENT>" : "there"
    }, it looks like there are folks from your team that haven't filled out the snapshot yet. Could you follow up with folks to make sure they respond?`;
  }

  if (target === "contributor") {
    if (pastDeadline) {
      return `Hey ${
        slack ? "<@RECIPIENT>" : "there"
      }, we're trying to close the snapshot and it looks like you haven't filled it out yet. Could you fill it out when you have a minute?`;
    }

    return `Hey ${
      slack ? "<@RECIPIENT>" : "there"
    }, just wanted to reach out because it looks like you haven't filled out the snapshot yet. Could you fill it out when you have a minute?`;
  }

  if (target === "triage_dri") {
    return `Hey ${
      slack ? "<@RECIPIENT>" : "there"
    }, I wanted to reach out because it looks like you haven't triaged your team's snapshot results yet.\n\nThis helps close the feedback loop and tells us where additional support is needed. Could you fill it out when you have a minute?`;
  }

  return "";
}

export function bulkNudge(args) {
  const {
    selectExpanded,
    targetGroupSelected,
    snapshotPastDeadline,
    usesSlack,
    snapshotId,
  } = args;

  return {
    selectExpanded,
    targetGroupSelected,
    snapshotPastDeadline,
    slackPreviewButtonText: null,
    messageChanged: false,
    audienceSize: null,
    loadingAudienceSize: false,
    confirmSendOpen: false,
    selectedSquadIds: [],
    selectedTagIds: [],
    init() {
      // Set up input event listener for real-time validation
      this.slackMessageTextArea.addEventListener("input", () =>
        this.validateMessage(),
      );
    },
    onSquadChange(squadIds) {
      this.selectedSquadIds = squadIds;
      if (this.targetGroupSelected) {
        this.fetchAudienceSize();
      }
    },
    onTagChange(tagIds) {
      this.selectedTagIds = tagIds;
      if (this.targetGroupSelected) {
        this.fetchAudienceSize();
      }
    },
    noSelectedOptions() {
      return !this.targetGroupSelected;
    },
    isSelected(targetGroup) {
      return targetGroup == this.targetGroupSelected;
    },
    select(targetGroup) {
      this.targetGroupSelected = targetGroup;
      this.setDefaultValue();
      this.selectExpanded = false;
      this.fetchAudienceSize();
    },
    get slackMessageTextArea() {
      return document.getElementsByTagName("textarea")[0];
    },
    setDefaultValue() {
      if (this.messageChanged) return;

      this.slackMessageTextArea.value = defaultNudgeMsg(
        this.targetGroupSelected,
        this.snapshotPastDeadline,
        usesSlack,
      );
      // Validate the default message
      this.validateMessage();
    },
    validateMessage() {
      const hasRecipientTag =
        !usesSlack && this.slackMessageTextArea.value.includes("<@RECIPIENT>");
      if (hasRecipientTag) {
        this.slackMessageTextArea.setCustomValidity(
          "Cannot use <@RECIPIENT> in MS Teams messages",
        );
      } else {
        this.slackMessageTextArea.setCustomValidity("");
      }
    },
    selectedRecipientSummary() {
      const count = this.audienceSize || 0;

      if (this.targetGroupSelected === "contributor") {
        return pluralizeWithCount("contributor", count);
      }

      if (this.targetGroupSelected === "leaf_dri") {
        return pluralizeWithCount("team lead", count);
      }

      if (this.targetGroupSelected === "parent_dri") {
        return pluralizeWithCount("parent team lead", count);
      }

      return pluralizeWithCount("recipient", count);
    },
    openSendConfirmation() {
      if (!this.slackMessageTextArea.reportValidity()) return;
      this.confirmSendOpen = true;
    },
    closeSendConfirmation() {
      this.confirmSendOpen = false;
    },
    async fetchAudienceSize() {
      if (!this.targetGroupSelected) return;

      this.loadingAudienceSize = true;
      this.audienceSize = null;

      try {
        const params = new URLSearchParams({
          target: this.targetGroupSelected,
        });

        // Use stored squad and tag IDs from onChange callbacks
        this.selectedSquadIds.forEach((id) => params.append("squad_ids[]", id));
        this.selectedTagIds.forEach((id) => params.append("tag_ids[]", id));

        const response = await fetch(
          `/snapshot/${snapshotId}/nudge_audience_size?${params.toString()}`,
        );
        const data = await response.json();
        this.audienceSize = data.count;
      } catch (error) {
        console.error("Failed to fetch audience size:", error);
        this.audienceSize = null;
      } finally {
        this.loadingAudienceSize = false;
      }
    },
  };
}
